import { message } from 'antd';
import { UserRole } from '../constants/user';
import { logError } from './logger';

// Token storage keys
const TOKEN_KEY = 'law_firm_crm_token';
const USER_INFO_KEY = 'law_firm_crm_user_info';
const REFRESH_TOKEN_KEY = 'law_firm_crm_refresh_token';

// User interface
export interface UserInfo {
  id: number;
  username: string;
  email: string;
  name: string;
  real_name?: string;
  role: UserRole;
  roles?: UserRole[]; // 全量角色（基础业务角色 + 管理员附加角色）
  department?: string;
  avatar?: string;
  permissions: string[];
  in_target_roster?: boolean;
}

// Auth response interface
export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user?: UserInfo;
  expiresIn?: number;
}

/**
 * Get token from localStorage
 */
export const getToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    logError('Error getting token from localStorage', error);
    return null;
  }
};

/**
 * Set token to localStorage
 */
export const setToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error('Error setting token:', error);
    message.error('无法保存登录信息');
  }
};

/**
 * Remove token from localStorage
 */
export const removeToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_INFO_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Error removing token:', error);
  }
};

/**
 * Get refresh token from localStorage
 */
export const getRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Error getting refresh token:', error);
    return null;
  }
};

/**
 * Set refresh token to localStorage
 */
export const setRefreshToken = (refreshToken: string): void => {
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  } catch (error) {
    console.error('Error setting refresh token:', error);
  }
};

/**
 * Get user info from localStorage
 */
export const getUserInfo = (): UserInfo | null => {
  try {
    const userInfoStr = localStorage.getItem(USER_INFO_KEY);
    if (userInfoStr) {
      return JSON.parse(userInfoStr);
    }
    return null;
  } catch (error) {
    console.error('Error getting user info:', error);
    return null;
  }
};

/**
 * Set user info to localStorage
 */
export const setUserInfo = (userInfo: UserInfo): void => {
  try {
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  } catch (error) {
    console.error('Error setting user info:', error);
    message.error('无法保存用户信息');
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = getToken();
  return !!token;
};

/**
 * Check if user has specific permission
 */
export const hasPermission = (permission: string): boolean => {
  const userInfo = getUserInfo();
  if (!userInfo || !userInfo.permissions) {
    return false;
  }
  return userInfo.permissions.includes(permission) || userInfo.permissions.includes('*');
};

/**
 * Check if user has any of the specified permissions
 */
export const hasAnyPermission = (permissions: string[]): boolean => {
  return permissions.some(permission => hasPermission(permission));
};

/**
 * Check if user has all of the specified permissions
 */
export const hasAllPermissions = (permissions: string[]): boolean => {
  return permissions.every(permission => hasPermission(permission));
};

/**
 * Get user role
 */
export const getUserRole = (): UserRole | null => {
  const userInfo = getUserInfo();
  return userInfo?.role || null;
};

/**
 * Get all roles of current user (primary role + additional roles)
 * 兼容旧 localStorage 中没有 roles 字段的用户信息（回退到主角色）
 */
export const getAllRoles = (): UserRole[] => {
  const userInfo = getUserInfo();
  if (!userInfo) return [];
  const additional = userInfo.roles || [];
  return userInfo.role ? [userInfo.role, ...additional] : [...additional];
};

/**
 * Check if user has specific role
 */
export const hasRole = (role: UserRole): boolean => {
  return getAllRoles().includes(role);
};

/**
 * Check if user has any of the specified roles
 * 匹配主角色或任一附加角色
 */
export const hasAnyRole = (roles: UserRole[]): boolean => {
  const all = getAllRoles();
  return roles.some(r => all.includes(r));
};

/**
 * 线索域管理员（admin 或 staff_admin 附加角色）
 */
export const isLeadAdmin = (): boolean =>
  hasAnyRole([UserRole.ADMIN, UserRole.STAFF_ADMIN]);

/**
 * 案件域管理员（admin 或 lawyer_admin 附加角色）
 */
export const isCaseAdmin = (): boolean =>
  hasAnyRole([UserRole.ADMIN, UserRole.LAWYER_ADMIN]);

/**
 * 财务域管理员（admin 或 finance_admin 附加角色）
 */
export const isFinanceAdmin = (): boolean =>
  hasAnyRole([UserRole.ADMIN, UserRole.FINANCE_ADMIN]);

/**
 * 判断给定用户对象是否拥有任一指定角色（主角色 + 附加角色）
 */
export const userHasAnyRole = (
  user: Pick<UserInfo, 'role' | 'roles'> | null | undefined,
  roles: UserRole[]
): boolean => {
  if (!user) return false;
  const all = [user.role, ...(user.roles || [])].filter(Boolean) as UserRole[];
  return roles.some(r => all.includes(r));
};

/**
 * 基于用户对象的域管理员判断（用于页面内按钮显隐，替代 user.role === 'admin'）
 */
export const userIsLeadAdmin = (user: Pick<UserInfo, 'role' | 'roles'> | null | undefined): boolean =>
  userHasAnyRole(user, [UserRole.ADMIN, UserRole.STAFF_ADMIN]);

export const userIsCaseAdmin = (user: Pick<UserInfo, 'role' | 'roles'> | null | undefined): boolean =>
  userHasAnyRole(user, [UserRole.ADMIN, UserRole.LAWYER_ADMIN]);

export const userIsFinanceAdmin = (user: Pick<UserInfo, 'role' | 'roles'> | null | undefined): boolean =>
  userHasAnyRole(user, [UserRole.ADMIN, UserRole.FINANCE_ADMIN]);

/**
 * Save authentication data
 */
export const saveAuthData = (authResponse: AuthResponse): void => {
  setToken(authResponse.access_token);
  setRefreshToken(authResponse.refresh_token);
  // User info will be fetched separately if needed
  if (authResponse.user) {
    setUserInfo(authResponse.user);
  }
};

/**
 * Clear all authentication data
 */
export const clearAuthData = (): void => {
  removeToken();
};

/**
 * Check if token is expired (basic check)
 */
export const isTokenExpired = (): boolean => {
  const token = getToken();
  if (!token) return true;

  try {
    // Basic JWT payload decode (without verification)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

/**
 * Get token expiration time
 */
export const getTokenExpirationTime = (): number | null => {
  const token = getToken();
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error('Error getting token expiration time:', error);
    return null;
  }
};

/**
 * Format user display name
 */
export const formatUserDisplayName = (user?: UserInfo | null): string => {
  if (!user) return '未知用户';
  return user.name || user.username || user.email || '未知用户';
};